import type {
  CreateUsHtsInput,
  AdditionalHtsInput,
  HtsDescriptionInput,
  HtsRateInput,
  PgaRequirementInput,
  HtsSpiRateInput,
  HtsFeeInput,
  IdInput,
  AdditionalHtsType,
} from '../../__generated__/graphql';

interface FormDateField {
  format?: (format: string) => string;
}

interface FormAdditional {
  number: string;
  type: AdditionalHtsType;
  startDate?: FormDateField | string;
  endDate?: FormDateField | string;
  include?: string[];
  exclude?: string[];
  description?: string;
  exportGroup?: string;
}

interface FormDescription {
  startDate?: FormDateField | string;
  endDate?: FormDateField | string;
  text?: string;
}

interface FormRate {
  rateCode?: string;
  adValorem?: number;
  specific?: number;
  startDate?: FormDateField | string;
  endDate?: FormDateField | string;
}

interface FormPga {
  pgaFlag: string;
  startDate?: FormDateField | string;
  endDate?: FormDateField | string;
}

interface FormSpiRate {
  adValorem?: number;
  code?: string;
  country?: string;
  other?: number;
  rateCode?: string;
  specific?: number;
  startDate?: FormDateField | string;
  endDate?: FormDateField | string;
}

interface FormFee {
  class?: string;
  duty?: string;
  adValorem?: number;
  other?: number;
  specific?: number;
  flag?: string;
  startDate?: FormDateField | string;
  endDate?: FormDateField | string;
}

const formatDate = (date: any) => date?.format?.('YYYY-MM-DD') || date;

export const transformUsHts = {
  toCreateSchema: (params: any): CreateUsHtsInput => {
    const { input } = params;
    if (!input) throw new Error('Input is required for US HTS creation');

    return {
      number: input.number,
      startDate: formatDate(input.startDate),
      endDate: formatDate(input.endDate),
      uom: input.uom,
      additionalHtssApplicable: (input.additional || []).map(
        (add: FormAdditional): AdditionalHtsInput => ({
          number: add.number,
          type: add.type,
          startDate: formatDate(add.startDate),
          endDate: formatDate(add.endDate),
          include: add.include,
          exclude: add.exclude,
          additionalTypeDescription: add.description,
          exportGroup: add.exportGroup,
        }),
      ),
      adCvdCases: (input.adCvdCases || []).map(
        (caseId: string): IdInput => ({ id: caseId }),
      ),
      description: input.description,
      fullyQualified: input.fullyQualified || false,
      additionalHTS: input.additionalHTS || false,
      descriptions: (input.descriptions || []).map(
        (desc: FormDescription): HtsDescriptionInput => ({
          startDate: formatDate(desc.startDate),
          endDate: formatDate(desc.endDate),
          text: desc.text,
        }),
      ),
      mainDutyRates: (input.mainDutyRates || []).map(
        (rate: FormRate): HtsRateInput => ({
          rateCode: rate.rateCode,
          adValorem: rate.adValorem,
          specific: rate.specific,
          startDate: formatDate(rate.startDate),
          endDate: formatDate(rate.endDate),
        }),
      ),
      pga: (input.pga || []).map(
        (pga: FormPga): PgaRequirementInput => ({
          flag: pga.pgaFlag,
          startDate: formatDate(pga.startDate),
          endDate: formatDate(pga.endDate),
        }),
      ),
      spiRates: (input.spiRates || []).map(
        (spi: FormSpiRate): HtsSpiRateInput => ({
          adValorem: spi.adValorem,
          code: spi.code,
          country: spi.country,
          other: spi.other,
          rateCode: spi.rateCode,
          specific: spi.specific,
          startDate: formatDate(spi.startDate),
          endDate: formatDate(spi.endDate),
        }),
      ),
      fees: (input.fees || []).map(
        (fee: FormFee): HtsFeeInput => ({
          class: fee.class,
          duty: fee.duty,
          feeAdValorem: fee.adValorem,
          feeOther: fee.other,
          feeSpecific: fee.specific,
          flag: fee.flag,
          startDate: formatDate(fee.startDate),
          endDate: formatDate(fee.endDate),
        }),
      ),
    };
  },
};
